import React, { useState, useEffect } from "react";
import { Select, Checkbox, Button, message, Spin } from "antd"; // ← added Spin
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Timesheetapi } from "../../middleware/interceptor";

const ALL_MODULES = [
    { key: "dashboard",        label: "Dashboard" },
    { key: "activity_card",    label: "Activity Card" },
    { key: "my_activities",    label: "My Activities" },
    { key: "teams_activities", label: "Teams Activities" },
    { key: "approvals",        label: "Approvals" },
    { key: "reports",          label: "Reports" },
    { key: "settings",         label: "Settings" },
    { key: "clients",          label: "Clients",  sub: true },
    { key: "projects",         label: "Projects", sub: true },
    { key: "users",            label: "Users",    sub: true },
    { key: "role",             label: "Role",     sub: true },
];

export default function Role() {
    const [roleOptions,    setRoleOptions]    = useState([]);
    const [selectedRole,   setSelectedRole]   = useState(null);
    const [checkedModules, setCheckedModules] = useState([]);
    const [loading,        setLoading]        = useState(false);
    const [saveLoading,    setSaveLoading]    = useState(false);
    const [resetLoading,   setResetLoading]   = useState(false);
    const [messageApi,     contextHolder]     = message.useMessage();

    // ── 1. Load roles → auto-select Admin ────────────────────────────
    const loadRoles = async () => {
        try {
            const response = await Timesheetapi.get("api/Roles/GetExistingRoles");
            const opts = (response.data?.Roles || []).map(r => ({
                value: r.emp_role_id,
                label: r.rolename,
            }));
            setRoleOptions(opts);

            if (opts.length > 0) {
                const adminOpt  = opts.find(r => r.label.toLowerCase() === "admin");
                const defaultOpt = adminOpt || opts[0];
                setSelectedRole(defaultOpt.value);
                loadPermissions(defaultOpt.value);
            }
        } catch (err) {
            console.error("Failed to load roles:", err);
            messageApi.open({ type: "error", content: "Failed to load roles." });
        }
    };

    useEffect(() => {
        loadRoles();
    }, []);

    // ── 2. Fetch permissions from DB ──────────────────────────────────
    const loadPermissions = async (empRoleId) => {
        setLoading(true);
        try {
            const response = await Timesheetapi.get(
                `api/Roles/GetPermissions?empRoleId=${empRoleId}`
            );
            if (response.data?.Error) {
                messageApi.open({ type: "error", content: response.data.Error });
                setCheckedModules([]);
                return;
            }
            const enabled = (response.data?.permissions || [])
                .filter(p => p.has_access)
                .map(p => p.module_key);
            setCheckedModules(enabled);
        } catch (err) {
            console.error("Failed to load permissions:", err);
            messageApi.open({ type: "error", content: "Failed to load permissions." });
        } finally {
            setLoading(false);
        }
    };

    // ── Role dropdown change ──────────────────────────────────────────
    const handleRoleChange = (value) => {
        setSelectedRole(value);
        loadPermissions(value);
    };

    // ── Checkbox toggle ───────────────────────────────────────────────
    const handleCheckboxChange = (key, checked) => {
        setCheckedModules(prev => {
            if (key === "settings" && !checked) {
                const subKeys = ALL_MODULES.filter(m => m.sub).map(m => m.key);
                return prev.filter(k => k !== "settings" && !subKeys.includes(k));
            }
            return checked ? [...prev, key] : prev.filter(k => k !== key);
        });
    };

    // ── Select All ────────────────────────────────────────────────────
    const isAllChecked    = checkedModules.length === ALL_MODULES.length;
    const isIndeterminate = checkedModules.length > 0 && checkedModules.length < ALL_MODULES.length;
    const handleSelectAll = (e) =>
        setCheckedModules(e.target.checked ? ALL_MODULES.map(m => m.key) : []);

    // ── Save ──────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!selectedRole) {
            messageApi.open({ type: "warning", content: "Please select a role first.", duration: 2 });
            return;
        }
        setSaveLoading(true);
        try {
            const response = await Timesheetapi.post("api/Roles/UpdatePermissions", {
                empRoleId:      selectedRole,
                checkedModules: checkedModules,
            });
            if (response.data?.success) {
                toast.success("Permissions saved successfully!");
            } else {
                toast.error(response.data?.message || "Save failed.");
            }
        } catch (err) {
            console.error("Save failed:", err);
            toast.error("Network error while saving.");
        } finally {
            setSaveLoading(false);
        }
    };

    // ── Reset ─────────────────────────────────────────────────────────
    const handleReset = async () => {
        if (!selectedRole) return;
        setResetLoading(true);
        try {
            const response = await Timesheetapi.post("api/Roles/ResetPermissions", {
                empRoleId: selectedRole,
            });
            if (response.data?.success) {
                const enabled = (response.data?.permissions || [])
                    .filter(p => p.has_access)
                    .map(p => p.module_key);
                setCheckedModules(enabled);
                messageApi.open({ type: "info", content: "Reset to default permissions.", duration: 1.5 });
            } else {
                toast.error(response.data?.message || "Reset failed.");
            }
        } catch (err) {
            console.error("Reset failed:", err);
            toast.error("Network error while resetting.");
        } finally {
            setResetLoading(false);
        }
    };

    const selectedLabel = roleOptions.find(r => r.value === selectedRole)?.label;

    // ── Render ────────────────────────────────────────────────────────
    return (
        <div>
            <ToastContainer />
            {contextHolder}

            {/* ✅ Spin wraps everything — shows centered overlay on Save/Reset */}
            <Spin
                spinning={saveLoading || resetLoading}
                tip={saveLoading ? "Saving..." : "Resetting..."}
                size="large"
            >
                <div className="mx-auto px-0">
                    <div className="mb-[5px]">
                        <h2 className="page-heading relative bottom-[-10px]">ROLE</h2>
                    </div>

                    <div
                        className="mt-8 bg-white rounded-lg border border-gray-200 shadow-sm p-6 w-[800px]"
                        style={{ margin: "0 auto" }}
                    >
                        {/* Role dropdown */}
                        <div className="mb-3">
                            <label className="text-[13px] font-[500] text-gray-600 block mb-2">
                                Select Role <span className="text-red-500">*</span>
                            </label>
                            <Select
                                style={{ width: 200 }}
                                placeholder="Select a role"
                                value={selectedRole}
                                onChange={handleRoleChange}
                                options={roleOptions}
                                loading={loading}
                                className="h-[32px]"
                            />
                        </div>

                        {selectedRole && (
                            <>
                                <div className="mb-3 flex items-center justify-between">
                                    <label className="text-[13px] font-[600] text-gray-700">
                                        Module Permissions
                                        <span className="ml-2 text-[11px] font-[400] text-blue-500">
                                            ({selectedLabel})
                                        </span>
                                    </label>
                                    <Checkbox
                                        checked={isAllChecked}
                                        indeterminate={isIndeterminate}
                                        onChange={handleSelectAll}
                                        className="text-[13px]"
                                    >
                                        Select All
                                    </Checkbox>
                                </div>

                                <div
                                    className="border border-gray-200 rounded-lg overflow-hidden"
                                    style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}
                                >
                                    {/* Header */}
                                    <div className="flex items-center bg-[#f1f5f9] px-4 py-2 border-b border-gray-200">
                                        <span className="text-[12px] font-[600] text-gray-500 uppercase tracking-wide flex-1">
                                            Module
                                        </span>
                                        <span className="text-[12px] font-[600] text-gray-500 uppercase tracking-wide w-[200px] flex justify-center">
                                            Access
                                        </span>
                                    </div>

                                    {/* Module rows */}
                                    {loading ? (
                                        <div className="text-center py-8 text-gray-400 text-[13px]">
                                            Loading permissions...
                                        </div>
                                    ) : (
                                        ALL_MODULES.map((module, index) => {
                                            const isChecked       = checkedModules.includes(module.key);
                                            const settingsChecked = checkedModules.includes("settings");

                                            // ✅ Sub modules always show — disabled when settings is off
                                            const isDisabled = module.sub && !settingsChecked;

                                            return (
                                                <div
                                                    key={module.key}
                                                    className={`flex items-center border-b border-gray-100 last:border-b-0 ${
                                                        index % 2 === 0 ? "bg-white" : "bg-[#fafafa]"
                                                    } ${!isDisabled ? "hover:bg-blue-50" : ""} transition-colors`}
                                                    style={{ paddingTop: "8px", paddingBottom: "8px", paddingRight: "16px" }}
                                                >
                                                    <div
                                                        className="flex items-center gap-2 flex-1"
                                                        style={{ paddingLeft: module.sub ? "32px" : "16px" }}
                                                    >
                                                        {module.sub ? (
                                                            // ✅ Arrow greyed out when disabled
                                                            <span className={`text-[11px] ${isDisabled ? "text-gray-200" : "text-gray-300"}`}>
                                                                └
                                                            </span>
                                                        ) : (
                                                            <span className={`w-2 h-2 rounded-full inline-block flex-shrink-0 ${
                                                                isChecked ? "bg-green-500" : "bg-gray-300"
                                                            }`} />
                                                        )}
                                                        {/* ✅ Label greyed out when disabled */}
                                                        <span className={`text-[13px] font-[450] ${
                                                            isDisabled
                                                                ? "text-gray-300"
                                                                : module.sub
                                                                    ? "text-gray-500"
                                                                    : "text-gray-700"
                                                        }`}>
                                                            {module.label}
                                                        </span>
                                                    </div>

                                                    <div style={{ width: "200px", display: "flex", justifyContent: "center" }}>
                                                        {/* ✅ Checkbox disabled when settings is off */}
                                                        <Checkbox
                                                            checked={isChecked}
                                                            disabled={isDisabled}
                                                            onChange={(e) => handleCheckboxChange(module.key, e.target.checked)}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Summary */}
                                <div className="mt-3 text-[12px] text-gray-500">
                                    <span className="text-green-600 font-[500]">{checkedModules.length}</span>
                                    {" "}of{" "}
                                    <span className="font-[500]">{ALL_MODULES.length}</span>
                                    {" "}modules enabled
                                </div>

                                {/* Buttons */}
                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        type="default"
                                        onClick={handleReset}
                                        loading={resetLoading}
                                        className="w-[100px]"
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={handleSave}
                                        loading={saveLoading}
                                        className="w-[100px]"
                                    >
                                        Save
                                    </Button>
                                </div>
                            </>
                        )}

                        {!selectedRole && (
                            <div className="text-center py-10 text-gray-400">
                                <i className="fa fa-user-shield text-[40px] mb-3 block opacity-30"></i>
                                <p className="text-[13px]">Select a role above to manage permissions</p>
                            </div>
                        )}
                    </div>
                </div>
            </Spin>
        </div>
    );
}